// Every tunable of the 왁뿌볼 crush engine. Lengths are in R (ball radius, CSS px) unless noted.

export const N = 64; // rim points
export const SUBSTEPS = 6;
export const DT = 1 / 60;
export const ACC_CLAMP = 0.1;
export const MAX_STEPS = 4;

export const TOP_WALL = 90; // px, keeps balls clear of the navbar pills
export const BOTTOM_RESERVE = 56; // px kept clear above the bottom edge for the "뿌순 개수" counter
export const BOTTOM_RESERVE_MOBILE = 64;

export const DRIFT_MAX = 40; // px/s
export const DAMP = 0.9975; // per 1/60 s
export const WALL_REST = 0.5;
export const DRAG = 1.6; // velocity damping per second
export const WALL_PUSH = 6; // inward push near a wall, R/s²
export const REPEL = 8; // ball-to-ball spacing push
export const REPEL_RANGE = 1.45; // …acting within this multiple of the contact distance
export const HOVER_RADIUS = 1.4;
export const HOVER_PUSH = 0.3; // R per second

export const FINGER_R = 0.45; // finger disc radius in R

// dent depth (fraction of R) per beat
export const D_PRESS = 0.05;
export const D_RESIST = 0.14;
export const D_CRACK = 0.2;
export const D_POP = 0.34;
export const D_CLAY_MAX = 0.45;
export const D_CLAY_RATE = 0.34; // R per second
export const SIGMA_SHELL = 0.45; // rad
export const SIGMA_CLAY = 0.6;

export const S_RESIST = 0.92;
export const S_CRACK = 0.88;
export const S_POP = 0.64;
export const SQUASH_TAU = 0.04; // s
export const RELEASE_KEEP = 0.87;
export const RELEASE_TAU = 0.2;

export const GAUGE_DOWN = 0.1;
export const GAUGE_REPRESS = 0.2;
export const GAUGE_GAIN = 1.6; // per second
export const GAUGE_DECAY = 0.6;
export const GAUGE_CRACK = 0.3;
export const GAUGE_POP = 0.7;
export const HOLD_CRACK_MS = 90;
export const HOLD_POP_MS = 250;
export const REPRESS_WINDOW_MS = 400;

export const POP_MS = 90;
export const SLOWMO_MS = 220;
export const SLOWMO_SCALE = 0.35;
export const PUNCH_MS = 160;
export const SHAKE_PX = 2;
export const SHAKE_MS = 120;

export const BURST_MIN_MS = 260;
export const BURST_MAX_MS = 420;
export const DRAG_THRESHOLD = 9; // px

export const CLAY_FRACTION = 0.65;

export const DETACH_BUDGET = 0.25; // at most this share of a ball's plates ever falls off

// 끈끈하게: dragging a popped ball peels cracked plates along on short clay necks (lengths in R)
export const NECK_LAG_MIN = 0.1; // a peeled chip trails the finger by 0.1–0.25 R…
export const NECK_LAG_MAX = 0.25;
export const NECK_SNAP_MIN = 0.28; // …and its neck snaps (툭) after 0.28–0.5 R of stretch
export const NECK_SNAP_MAX = 0.5;
export const NECK_WAIST_MIN = 0.45; // waist radius right before the snap, as a fraction of the rest radius
export const NECK_V_MIN = 0.5; // finger speed over the ball (R/s) needed to peel a plate
export const NECK_MAX_ACTIVE = 2; // stretching necks per ball at once
export const NECK_GRAB_MS = 170; // min gap between two peels on one ball
export const NECK_RETRACT_MS = 120; // the snapped stub sinks back into the ball
export const CHIP_MAX = 10; // chips per ball, embedded ones included
export const CHIP_DROP_MAX = 1.5; // a detached chip falls at most this far while it fades
export const CHIP_FADE_MS = 450;

export const CLAY = "#EFE9DC";
export const CLAY_DARK = "#D9D2C3";
export const CLAY_SHEEN = "#F7F3EA";
export const CLAY_MUD = "#E3DCCB";
export const FRACTURE = "#F4F6F8";
export const CRUMB = "#E8EAF0";
export const CRAZE = "#F7F8FA";

export type ShellVariant = {
  name: string;
  base: string;
  swirl: [string, string];
  light: boolean; // light shells get darker fracture edges
  core: string; // the clay inside: always far from the shell colour, so cracking it open shows something new
};

export const VARIANTS: ShellVariant[] = [
  { name: "cobalt", base: "#3A4FE0", swirl: ["#6C7CF2", "#1E2DB0"], light: false, core: "#F4B63F" },
  { name: "ink", base: "#202236", swirl: ["#343759", "#0F1020"], light: false, core: "#F27A6B" },
  { name: "milk", base: "#F2F3F7", swirl: ["#E3E6EE", "#FAFBFF"], light: true, core: "#B23A24" },
  { name: "steel", base: "#AEB4C2", swirl: ["#C9CED9", "#8E95A6"], light: true, core: "#E2711F" },
  { name: "tricolour", base: "#3A4FE0", swirl: ["#F2F3F7", "#AEB4C2"], light: false, core: "#E5537F" },
];

/**
 * How a ball breaks. Every press damages only the plates under and around the finger (a soft disc the size of a
 * fingertip on screen), up to `dmgPerPress` at the centre of the touch, so the hole grows press by press where
 * you press: a press cracks the flake under the finger, the next one pops it off and cracks the ring around it.
 */
export type Pace = {
  seeds: [number, number]; // plates per ball (at most 256: plate ids are 8-bit)
  fine: number; // cells of the finer crack layer a crushed plate shatters along
  fineFrom: number; // damage level from which a plate's fine hairlines show (it reads as smaller pieces)
  seg: [number, number]; // shape grid (plastic memory) segments
  renderSeg: [number, number]; // render mesh segments (cap): fine enough that each shell piece shows its own lift and tilt
  res: 1 | 2 | 4; // texture scale of the crack field, the paint and the wax detail (2 = 2048×1024, 4 = 4096×2048):
  // about one texel per device pixel round the ball on a 3× phone, so nothing on it is ever magnified
  imp: [number, number]; // finger-mark map size (equirect)
  contactPx: number; // fingertip contact radius on screen (CSS px): the damage kernel's width
  dmgPerPress: number; // damage one firm press adds at the centre of the touch (a plate breaks off at 1)
  dmgTau: number; // s: a press delivers most of its damage within ~2 tau
  hairS: number; // hairline fade-in time
  openTau: number; // s, crack opening ease
  idleSpin: number; // rad/s: untouched balls turn slowly, so every side comes round to the finger
};

export const PACE: Pace = {
  seeds: [235, 250], fine: 750, fineFrom: 3, seg: [256, 128], renderSeg: [384, 192], res: 2, imp: [1536, 768],
  contactPx: 36, dmgPerPress: 0.62, dmgTau: 0.2, hairS: 0.08, openTau: 0.07, idleSpin: 0.18,
};

/** 왕왁뿌볼: one ball three times the size; small plates and a finer fragment layer, so a press breaks a smaller share. */
export const KING_PACE: Pace = {
  seeds: [245, 255], fine: 2800, fineFrom: 2, seg: [384, 192], renderSeg: [512, 256], res: 4, imp: [3072, 1536],
  contactPx: 40, dmgPerPress: 0.62, dmgTau: 0.22, hairS: 0.1, openTau: 0.09, idleSpin: 0.12,
};

/**
 * The shell's relief (fractions of the ball radius), shared by the vertex/fragment shaders (crackShader.ts) and the
 * CPU copy that seats sticky chips (ball3d.ts reliefAt). Real 왁뿌볼 wax is ~1–2 mm on a ~27 mm ball: 4–7 % of R.
 */
export const SH_THICK = 0.04; // the wax coat: the clay floor of an open crack sits this far below the plates
export const SH_LIFT = 0.02; // a loose plate stands up to this far proud of the clay…
export const SH_PEEL = 0.22; // …and its edge facing away from the finger peels up (R per rad from its centre)
export const SH_SINK = 0.018; // a crushed plate's fragments are pressed in this far…
export const SH_FRAG = 0.022; // …plus up to this much more, each fragment its own amount
/** the ring of clay a press pushes up just outside the dent: centre and width in dent sigmas */
export const RIM_RING = 2.0;
export const RIM_WIDTH = 0.45;

/** Damage levels of a plate: hairline → cracked → open → loose → crushed (DMG_GONE: it shatters but stays on). */
export const DMG_HAIR = 0.12;
export const DMG_CRACK = 0.3;
export const DMG_OPEN = 0.55;
export const DMG_LOOSE = 0.8;
export const DMG_GONE = 1;

export const MOBILE_WIDTH = 700;

export function ballRadiusBase(w: number, h: number) {
  const desktop = Math.min(130, Math.max(44, 0.11 * Math.min(w, h)));
  // portrait phones get much bigger balls (thumb-sized targets), 15% under double so two fit side by side
  let r = w < MOBILE_WIDTH && h >= w ? desktop * 2 * 0.85 : desktop;
  // never bigger than the grid can hold with room to squash and move (small phones in landscape, short windows)
  const n = ballCount(w), cols = n > 4 ? 3 : 2, rows = Math.ceil(n / cols);
  const playH = h - TOP_WALL - (w < MOBILE_WIDTH ? BOTTOM_RESERVE_MOBILE : BOTTOM_RESERVE);
  r = Math.min(r, w / cols / 2 / 1.25, playH / rows / 2 / 1.15);
  return Math.max(20, r);
}
export function ballCount(w: number) {
  return w < MOBILE_WIDTH ? 4 : 6;
}

/** 왕왁뿌볼 radius: three normal balls wide, but never past the screen (it still has to squash and drift). */
export function kingRadius(w: number, h: number) {
  const desktop = Math.min(130, Math.max(44, 0.11 * Math.min(w, h)));
  const normal = w < MOBILE_WIDTH && h >= w ? desktop * 2 * 0.85 : desktop;
  const playH = h - TOP_WALL - (w < MOBILE_WIDTH ? BOTTOM_RESERVE_MOBILE : BOTTOM_RESERVE);
  return Math.max(40, Math.min(normal * 3, (w / 2 - 10) / 1.1, playH / 2 / 1.12));
}
